'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

import { EventStatus, PostLuckyDrawResponse } from '@flipster/apis/v1/campaign/types';
import { Button } from '@flipster/react/design-system';
import { css } from '@flipster/styled-system/css';
import { center, flex, grid } from '@flipster/styled-system/patterns';

import { ImageOrLottie } from '@web/app/[locale]/campaign/[slug]/_sections/spooky-treats-festival/image-or-lottie';
import { spacing3, spacing6 } from '@web/app/[locale]/campaign/[slug]/styles';

import { LuckyDrawPrizeItemFragment } from '../../../query.generated';
import { useLuckyDraw, useSpinLuckyDraw, useSpookyTreatsFestivalLuckyDraw } from '../useLuckyDraw';

const slotSkinColor: Record<string, string> = {
  'spooky-treats-festival-lv1': '#C685DF',
  'spooky-treats-festival-lv2': '#FFFFFF',
  'spooky-treats-festival-lv3': '#F45C00',
};

/** 1 ~ numberOfItems까지 셔플해서 랜덤으로 순서 반환 */
function getShuffledNumbers(numberOfItems: number) {
  const numbers = Array.from({ length: numberOfItems }, (_, i) => i + 1);
  for (let i = numbers.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [numbers[i], numbers[j]] = [numbers[j], numbers[i]];
  }
  return numbers;
}

interface SpinMachineProps {
  prizes: LuckyDrawPrizeItemFragment[];
  slug: string;
  isShow: boolean;
}

export const SpinMachine = ({ prizes, slug, isShow }: SpinMachineProps) => {
  const [isRunning, setIsRunning] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState<number>(-1);
  const [revealedPrize, setRevealedPrize] = useState<LuckyDrawPrizeItemFragment | undefined>(undefined);

  const rafRef = useRef<number | null>(null);
  const focusRef = useRef<number>(-1);

  // spin control refs (새로 추가)
  const phaseRef = useRef<'accel' | 'plateau' | 'final'>('accel');
  const lapRef = useRef<number>(0);
  const stepIdxRef = useRef<number>(0);
  const sequenceRef = useRef<number[]>([]);
  const nextTickAtRef = useRef<number>(0);
  const pendingFinalRef = useRef<boolean>(false);
  const winnerPrizeIndexRef = useRef<number | null>(null);
  const winnerDataRef = useRef<PostLuckyDrawResponse | null>(null);

  const maxInterval = 150; // 느릴 때
  const minInterval = 100; // 빠를 때
  const endInterval = 500;

  const { mutateAsync: postLuckyDraw } = useSpinLuckyDraw(slug);
  const { status, luckyDrawData } = useSpookyTreatsFestivalLuckyDraw();
  const { refetch: refetchLuckyDraw } = useLuckyDraw(slug);

  const data = luckyDrawData.find((data) => data?.id === slug);

  // 룰렛 돌리는 중이거나 이벤트 진행중이 아니거나 토큰이 없으면 버튼 비활성화
  const buttonDisabled = isRunning || data?.luckyDrawEventStatus !== 'ONGOING' || !data?.token;

  const onClickGo = async () => {
    if (isRunning) return;
    // 당첨 상품 초기화
    setRevealedPrize(undefined);
    // 즉시 회전 시작 (당첨 정보 없이)
    start();
    // 서버 호출은 비동기로 진행, 응답 오면 다음 바퀴를 당첨으로 설정
    try {
      const { data } = await postLuckyDraw();
      winnerPrizeIndexRef.current = prizes.findIndex((prize) => prize.id === data.prizeId);
      winnerDataRef.current = data;
      pendingFinalRef.current = true; // 다음 바퀴를 'final'로 전환
    } catch {
      // 회전 중이면 즉시 중단
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
        setFocusedIndex(-1);
      }
      setIsRunning(false);
    }
  };

  const stop = useCallback(() => {
    setIsRunning(false);
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }

    refetchLuckyDraw();
  }, [refetchLuckyDraw]);

  const start = useCallback(() => {
    const numberOfItems = prizes.length;
    // 초기화
    phaseRef.current = 'accel';
    lapRef.current = 0;
    stepIdxRef.current = 0;
    pendingFinalRef.current = false;
    winnerPrizeIndexRef.current = null;
    winnerDataRef.current = null;

    // 첫 바퀴 시퀀스 준비 (셔플)
    sequenceRef.current = getShuffledNumbers(numberOfItems);

    // 시작 포커스 지정
    const first = sequenceRef.current[0] - 1;
    focusRef.current = first;
    setFocusedIndex(first);

    setIsRunning(true);
    nextTickAtRef.current = performance.now(); // 첫 스텝 즉시

    const clamp01 = (x: number) => Math.min(1, Math.max(0, x));
    const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
    const easeInCubic = (t: number) => t * t * t;
    const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

    const computeInterval = (phase: 'accel' | 'plateau' | 'final', stepIdx: number) => {
      if (phase === 'plateau') return minInterval;
      const t = clamp01((stepIdx + 1) / numberOfItems);
      if (phase === 'accel') return lerp(maxInterval, minInterval, easeInCubic(t));
      // final
      return lerp(minInterval, endInterval, easeOutCubic(t));
    };

    const loop = (now: number) => {
      if (now >= nextTickAtRef.current) {
        // 현재 스텝 반영
        const seq = sequenceRef.current;
        const idxInLap = stepIdxRef.current;
        const next = seq[idxInLap] - 1;
        focusRef.current = next;
        setFocusedIndex(next);

        // 스텝 진행
        stepIdxRef.current = idxInLap + 1;

        // final 바퀴의 마지막 스텝 완료 시 종료
        if (phaseRef.current === 'final' && stepIdxRef.current >= numberOfItems) {
          setTimeout(() => {
            const finalIndex = winnerPrizeIndexRef.current ?? focusRef.current;
            setRevealedPrize(prizes[finalIndex]);
            if (winnerDataRef.current) {
              stop();
            } else {
              // 안전 중단
              setIsRunning(false);
              if (rafRef.current) {
                cancelAnimationFrame(rafRef.current);
                rafRef.current = null;
              }
            }
          }, 500);
          return;
        }

        // 한 바퀴 완료 시 다음 바퀴 준비
        if (stepIdxRef.current >= numberOfItems) {
          lapRef.current += 1;
          stepIdxRef.current = 0;

          if (pendingFinalRef.current && phaseRef.current !== 'final') {
            // 다음 바퀴를 당첨 바퀴로 설정하고 감속 모드로 전환
            const seqNext = getShuffledNumbers(numberOfItems);
            const win = winnerPrizeIndexRef.current ?? 0;
            seqNext[numberOfItems - 1] = win + 1; // 마지막 칸을 당첨 인덱스로
            sequenceRef.current = seqNext;
            phaseRef.current = 'final';
            pendingFinalRef.current = false;
          } else {
            // 계속 대기 (두 번째 바퀴부터 plateau 유지)
            sequenceRef.current = getShuffledNumbers(numberOfItems);
            if (lapRef.current >= 1) phaseRef.current = 'plateau';
          }
        }

        // 다음 틱 시간 계산
        const interval = computeInterval(phaseRef.current, stepIdxRef.current);
        nextTickAtRef.current = now + interval;
      }

      rafRef.current = requestAnimationFrame(loop);
    };

    rafRef.current = requestAnimationFrame(loop);
  }, [maxInterval, minInterval, endInterval, stop, prizes]);

  useEffect(() => {
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  if (!isShow) return null;

  return (
    <div
      className={flex({
        flexDirection: 'column',
        alignItems: 'center',
        gap: { base: 20, tablet: 16 },
        pt: { base: 20, tablet: 32 },
        w: { tabletDown: '100%' },
      })}
    >
      <div
        className={center({
          gap: 10,
          p: 12,
          border: '4px solid',
          borderColor: 'border.accent.info',
          rounded: 16,
          bgColor: 'background.default.level-1',
          w: { base: '100%', tablet: 500 },
          aspectRatio: '1 / 1',
        })}
        style={{
          borderColor: slotSkinColor[slug],
        }}
      >
        <GridItems prizes={prizes} focusedIndex={focusedIndex} status={status} revealedPrize={revealedPrize} />
      </div>

      <Button
        hierarchy='primary'
        color='accent'
        size='large'
        disabled={buttonDisabled}
        label='Pick your reward'
        onClick={onClickGo}
      />
    </div>
  );
};

interface GridItemsProps {
  prizes: LuckyDrawPrizeItemFragment[];
  focusedIndex: number;
  status?: EventStatus;
  revealedPrize: LuckyDrawPrizeItemFragment | undefined;
}

/** spin machine 내부 아이템 렌더링 */
const GridItems = ({ prizes, focusedIndex, revealedPrize }: GridItemsProps) => {
  // 당첨된 상품이 있을 때
  if (revealedPrize) return <RevealedPrize revealedPrize={revealedPrize} />;

  return (
    <div className={grid({ gap: 8, columns: 3 })}>
      {prizes.map((prize, idx) => {
        const focused = idx === focusedIndex;
        return (
          <div
            key={`${prize.name}-${idx}`}
            className={center({
              pos: 'relative',
              bgColor: focused ? 'background.accent.primary.tint' : 'background.default.level-2',
              rounded: 8,
              transition: 'transform 120ms ease, box-shadow 120ms ease',
              userSelect: 'none',
              border: focused ? '1px solid' : 'none',
              borderColor: 'border.accent.promotion',
              aspectRatio: '1 / 1',
              p: 12,
            })}
          >
            {prize.image?.url && (
              <ImageOrLottie url={prize.image.url ?? ''} alt={prize.name ?? 'prize image'} css={{ w: '100%' }} />
            )}
          </div>
        );
      })}
    </div>
  );
};

const RevealedPrize = ({ revealedPrize }: { revealedPrize: LuckyDrawPrizeItemFragment }) => {
  return (
    <div className={css({ flexDirection: 'column', gap: spacing3, textAlign: 'center', whiteSpace: 'pre-line' })}>
      {revealedPrize.image?.url && (
        <ImageOrLottie
          url={revealedPrize.image.url ?? ''}
          alt={revealedPrize.name ?? 'prize image'}
          css={{ w: { base: 140, tablet: 200 } }}
        />
      )}
      <p
        className={css({
          textStyle: 'Title/S',
          color: 'content.default.level-4',
          mt: { base: spacing3, tablet: spacing6 },
          mb: 4,
        })}
      >
        {revealedPrize.resultMessage}
      </p>
      <p
        className={css({
          textStyle: 'Text/L/Regular',
          color: 'content.default.level-3',
        })}
      >
        {revealedPrize.resultDescription}
      </p>
    </div>
  );
};
